import styled from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';

import { SelectRarity } from 'atoms/SelectRarity';

const AUTHORIZED_RARITIES = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
];

const Root = styled(FullViewportScroll)`
  position: sticky;
  top: var(--current-stack-height);
  z-index: 1;
  background: var(--c-black);
`;

const Container = styled.div`
  --filter-height: 22px;
  --filter-min-width: 56px;
  display: grid;
  width: 100%;
  grid-template-columns: 74px 1fr;
  gap: 0;
  padding: var(--double-unit) 0;
`;

const StyledTabBar = styled(TabBar)`
  --active-fg-color: var(--c-white);
  --active-bg-color: var(--c-nd-200);
  --bar-gap-custom: var(--unit);

  &.fullWidth:not(.forceMobileLayout) > ul {
    padding-left: 0;
  }
`;

const RarityFilter = styled(Horizontal)`
  position: relative;
  isolation: isolate;
  z-index: 1;
  background: var(--c-black);
`;

const CompetitionLogo = styled.img`
  height: var(--filter-height);
  .selected & {
    filter: grayscale(1) invert(1);
  }
`;
const TabContent = styled(Horizontal)`
  min-width: var(--filter-min-width);
`;
const StyledStarBall = styled(StarBall)`
  height: var(--filter-height);
  width: var(--filter-height);
  .selected & {
    filter: grayscale(1) invert(1);
  }
`;
const StyledTab = styled(Tab)`
  flex-grow: 1 !important;
`;

export const Filters = ({ sport }: { sport: Sport }) => {
  const { scoutableSo5Competitions } = useSo5Competitions();

  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const rarity =
    lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.rarity || Rarity.limited;
  const competitionSlug =
    lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.competitionSlug || '';

  const options = [
    {
      label: <StyledStarBall />,
      selectedLabel: 'All Star',
      value: 'DEFAULT',
    },
    ...scoutableSo5Competitions.map(competition => ({
      label: (
        <CompetitionLogo
          src={competition.iconUrl}
          alt={competition.displayName}
        />
      ),
      selectedLabel: competition.displayName,
      value: competition.slug,
    })),
  ];

  const onChangeRarity = (r: Rarity) => {
    updateLifecycle(LIFECYCLE.marketHomeFilters, {
      [sport]: {
        ...lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport],
        rarity: r,
      },
    });
  };
  const onChangeCompetition = (c: string) => {
    updateLifecycle(LIFECYCLE.marketHomeFilters, {
      [sport]: {
        ...lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport],
        competitionSlug: c,
      },
    });
  };

  const hideCompetitionFilter = scoutableSo5Competitions.length < 2;

  return (
    <Root>
      <Container>
        <RarityFilter>
          <SelectRarity
            rarities={AUTHORIZED_RARITIES}
            selectedRarity={rarity}
            onChange={onChangeRarity}
          />
        </RarityFilter>

        {!hideCompetitionFilter && (
          <FullViewportScroll>
            <StyledTabBar
              fullWidth
              value={competitionSlug}
              variant="segmented"
              hideBackgroundWhenNoValue
              scrollable
              onValueChange={onChangeCompetition}
              grow
            >
              {options.map(option => (
                <StyledTab
                  key={option.value}
                  value={option.value}
                  className={competitionSlug === option.value ? 'selected' : ''}
                >
                  <TabContent center as="span">
                    {option.label}
                    {competitionSlug === option.value && (
                      <LabelL bold>{option.selectedLabel}</LabelL>
                    )}
                  </TabContent>
                </StyledTab>
              ))}
            </StyledTabBar>
          </FullViewportScroll>
        )}
      </Container>
    </Root>
  );
};
